import {
  useWSModalCustom,
  WSActions,
  WSControlGroup,
  WSForm,
  WSInputNumber,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { PreferredPayoutSpeed } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import { queriesPayouts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { WSQueries } from "../../../query/WSQuery";
import { reversePercent } from "../utils";

type IncomeType = "all" | "no" | "some";

type FormData = {
  incomeType?: IncomeType;
  rate?: number | null;
};

export const ModalUpdateWalletDepositRate: React.FC<
  WSModalCustomProps<void>
> = props => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();

  const [updatePayoutSettings] =
    queriesPayouts.updatePayoutSettings.useMutation({
      onSuccess: () => {
        props.onClose();
      }
    });

  return (
    <WSModal
      title="How much of your income would you like deposited into your Wingspan Wallet account?"
      size="S"
      {...props}
    >
      <WSQueries
        queries={{
          queryPayoutSettings
        }}
      >
        {({ queryPayoutSettings: { data: payoutSettings } }) => {
          const destination = payoutSettings
            ? selectorPayoutSettingsDefaultDestination(payoutSettings)
            : undefined;
          const normalPercent = reversePercent(destination?.percentage);

          return (
            <WSForm<FormData>
              onSubmit={async formData => {
                const payoutSettingsId = payoutSettings?.id;
                if (!payoutSettingsId) {
                  return;
                }

                let walletDistribution: number;
                if (formData.incomeType === "all") {
                  walletDistribution = 100;
                } else if (formData.incomeType === "no") {
                  walletDistribution = 0;
                } else {
                  walletDistribution = formData.rate || 0;
                }

                const destinationUpdate =
                  walletDistribution >= 100 || !destination?.id
                    ? null
                    : {
                        type: destination.type,
                        id: destination.id,
                        percentage: 100 - walletDistribution
                      };

                const shouldSendRemainderToWallet = walletDistribution > 0;

                await updatePayoutSettings({
                  payoutSettingsId,
                  payoutSettingsUpdate:
                    payoutSettings?.preferredPayoutSpeed ===
                    PreferredPayoutSpeed.INSTANT
                      ? {
                          instantDestination: destinationUpdate,
                          shouldSendRemainderToWallet
                        }
                      : {
                          standardDestination: destinationUpdate,
                          shouldSendRemainderToWallet
                        }
                });
              }}
              defaultValues={{
                incomeType:
                  normalPercent === 100
                    ? "all"
                    : normalPercent === 0
                      ? "no"
                      : "some",
                rate:
                  normalPercent === 100
                    ? 30
                    : normalPercent === 0
                      ? 30
                      : normalPercent
              }}
              validationSchema={Yup.object().shape({
                rate: Yup.number().min(0).max(100).nullable()
              })}
            >
              <WSList gap="XL">
                <WSForm.Field
                  name="incomeType"
                  component={WSControlGroup}
                  componentProps={{
                    type: "radio",
                    chip: true,
                    options: [
                      {
                        label: "All income",
                        value: "all"
                      },
                      {
                        label: "No income",
                        value: "no"
                      },
                      {
                        label: "Some income",
                        value: "some"
                      }
                    ]
                  }}
                />

                <WSForm.Value name="incomeType">
                  {incomeType =>
                    incomeType === "some" ? (
                      <WSForm.Field
                        name="rate"
                        component={WSInputNumber}
                        label="Select income"
                        componentProps={{
                          size: "L",
                          iconRight: "percent",
                          align: "right",
                          min: 0,
                          max: 100
                        }}
                      />
                    ) : null
                  }
                </WSForm.Value>

                <WSList gap="S">
                  <WSActions
                    alignment="fill"
                    buttons={[
                      {
                        label: "Update deposit rate",
                        type: "submit",
                        kind: "Primary"
                      }
                    ]}
                  />
                  <WSText.ParagraphXs color="gray400">
                    Don’t worry, you can always change this again later.
                  </WSText.ParagraphXs>
                </WSList>
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};

export const useModalUpdateWalletDepositRate = () =>
  useWSModalCustom(ModalUpdateWalletDepositRate);
